/**
 * LegalDocumentPage (1.34.0) — публичная страница юридического документа.
 *
 * Используется на трёх маршрутах: /legal/privacy, /legal/consent, /legal/terms.
 * Каждый — отдельный компонент-обёртка, который только указывает kind.
 *
 * Доступ без авторизации (миграция legal_documents даёт публичный SELECT
 * через RLS-политику для anon).
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, F } from '../design-system/tokens';
import { Button } from '../design-system/atoms/Button';
import { Spinner } from '../design-system/atoms/Spinner';
import { MarkdownView } from '../design-system/atoms/MarkdownView';
import {
  fetchCurrentLegalDocument,
  type LegalDocument,
  type LegalDocumentKind,
} from '../domains/legal/api';

interface LegalDocumentPageProps {
  kind: LegalDocumentKind;
}

export function LegalDocumentPage({ kind }: LegalDocumentPageProps) {
  const navigate = useNavigate();
  const [doc, setDoc] = useState<LegalDocument | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const d = await fetchCurrentLegalDocument(kind);
        if (cancelled) return;
        setDoc(d);
        if (!d) setError('Документ не найден');
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Ошибка загрузки');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  return (
    <div style={{
      height: '100dvh',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      background: C.bg,
      padding: '20px 18px 48px',
    }}>
      {/* Шапка с кнопкой назад */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 20,
        maxWidth: 720,
        width: '100%',
      }}>
        <Button
          ch="← Назад"
          col="ghost"
          sz="sm"
          onClick={() => {
            // Если есть история — назад, иначе на корень
            if (window.history.length > 1) navigate(-1);
            else navigate('/');
          }}
        />
      </div>

      {/* Содержимое */}
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <Spinner label="Загружаем документ..." />
        </div>
      )}

      {error && !loading && (
        <div style={{
          padding: 20,
          background: C.reS,
          border: `1px solid ${C.re}55`,
          borderRadius: 12,
          color: C.re,
          maxWidth: 720,
        }}>
          {error}
        </div>
      )}

      {doc && !loading && (
        <div>
          <MarkdownView source={doc.body_md} />
          <div style={{
            marginTop: 32,
            paddingTop: 16,
            borderTop: `1px solid ${C.b}`,
            fontSize: 11,
            color: C.sub,
            fontFamily: F.mono,
          }}>
            Версия документа: {doc.version} · Опубликовано: {new Date(doc.published_at).toLocaleDateString('ru-RU')}
          </div>
        </div>
      )}
    </div>
  );
}

// Обёртки для каждого маршрута — чтобы регистрировать их в Routes одной строкой

export function PrivacyPage()  { return <LegalDocumentPage kind="privacy" />; }
export function ConsentPage()  { return <LegalDocumentPage kind="consent" />; }
export function TermsPage()    { return <LegalDocumentPage kind="terms"   />; }
